import React from 'react';

import { gql } from '@apollo/client';

import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';

import { Form } from '@repo/ui/Form';
import { TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { useAccount } from '@/hooks/useAccount';
import { getGraphQLErrorMessage } from '@repo/util/getGraphQLErrorMessage';

// FORM SCHEMA

const createPrimaryContactForAccountFormSchema = z.object({
  firstName: z.string().min(1, { message: 'Please include a first name.' }),
  lastName: z.string().min(1, { message: 'Please include a last name.' }),
  email: z.string().email({ message: 'Please submit a valid email address.' }),
  phoneNumber: z.string().min(7, { message: 'Please submit a valid phone number.' }),
  title: z.string().optional(),
  mobilePhoneNumber: z
    .string()
    .min(7, { message: 'Please submit a valid mobile phone number.' })
    .optional()
    .or(z.literal('')),
});

type CreatePrimaryContactForAccountFormParams = TypeOf<
  typeof createPrimaryContactForAccountFormSchema
>;

const CreateAccountContactForm = () => {
  const user = useUser();
  const userCanCreate = POLICIES['account_primary_contacts:create'](user);

  const currentAccount = useAccount();

  // Mutation
  const CREATE_PRIMARY_CONTACT_FOR_ACCOUNT = gql`
    mutation CreatePrimaryContactForAccount(
      $accountId: ID!
      $input: CreatePrimaryContactForAccountInput!
    ) {
      createPrimaryContactForAccount(accountId: $accountId, input: $input) {
        accountPrimaryContact {
          id
        }
      }
    }
  `;

  const client = useApolloClient();

  const onSubmit = async (input: CreatePrimaryContactForAccountFormParams) => {
    try {
      await client.mutate({
        mutation: CREATE_PRIMARY_CONTACT_FOR_ACCOUNT,
        variables: {
          input: {
            ...input,
            label: 'primary_contact',
          },
          accountId: currentAccount?.id,
        },
      });
    } catch (error) {
      throw new Error(
        getGraphQLErrorMessage(error) ??
          'Error while trying to create account primary contact. Please try again later or contact support if error persists.'
      );
    }
  };
  const form = useZodForm({
    schema: createPrimaryContactForAccountFormSchema,
    onSubmit,
  });

  return (
    <>
      <Form {...form} className="grid gap-6 grid-col-1 xs:grid-cols-2">
        <Form.Success>Account primary contact successfully created.</Form.Success>
        <Form.Error autoClose={15000} />
        <TextInputField label="Title" disabled={!userCanCreate} {...registerInput(form, 'title')} />
        <TextInputField
          label="First Name"
          disabled={!userCanCreate}
          {...registerInput(form, 'firstName')}
        />
        <TextInputField
          label="Last Name"
          disabled={!userCanCreate}
          {...registerInput(form, 'lastName')}
        />
        <TextInputField
          label="Email address"
          disabled={!userCanCreate}
          {...registerInput(form, 'email')}
        />
        <TextInputField
          label="Phone number"
          disabled={!userCanCreate}
          {...registerInput(form, 'phoneNumber')}
        />
        <TextInputField
          label="Mobile phone number"
          disabled={!userCanCreate}
          {...registerInput(form, 'mobilePhoneNumber')}
        />
        <div className="w-full">
          <Button type="submit" disabled={!userCanCreate}>
            Create Contact
          </Button>
        </div>
      </Form>
    </>
  );
};

export default CreateAccountContactForm;
